import { useEffect, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useUi } from '../../context/UiContext';
import { useI18n } from '../../i18n/useI18n';
import { toKey } from '../../utils/dates';
import { IconClose } from '../icons/Icons';
import styles from './overlays.module.css';

const UNITS = ['day', 'week', 'month', 'year'];

export function RecurModal() {
  const { actions } = useApp();
  const { ui, closeRecurCreate, openRecurring } = useUi();
  const { t } = useI18n();
  const draft = ui.recurCreate || {};
  const inputRef = useRef(null);
  const [text, setText] = useState(draft.text || '');
  const [every, setEvery] = useState(draft.every || 1);
  const [unit, setUnit] = useState(draft.unit || 'day');
  const [start, setStart] = useState(draft.start || toKey(new Date()));

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        closeRecurCreate();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [closeRecurCreate]);

  const count = Math.min(99, Math.max(1, Math.round(Number(every) || 1)));

  const save = (event) => {
    event.preventDefault();
    if (!text.trim() || !start) return;
    actions.saveRecurring({
      id: draft.id,
      text,
      every: count,
      unit,
      start,
      notes: draft.notes,
      doneOn: draft.doneOn,
      createdAt: draft.createdAt,
    });
    closeRecurCreate();
  };

  return (
    <>
      <div className={styles.scrim} onMouseDown={closeRecurCreate} />
      <form
        className={styles.recurModal}
        role="dialog"
        aria-modal="true"
        aria-label={t('recur.create')}
        onSubmit={save}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className={styles.recurTitle}>
          <input
            ref={inputRef}
            value={text}
            dir="auto"
            placeholder={t('recur.create')}
            aria-label={t('recur.create')}
            onChange={(event) => setText(event.target.value)}
          />
          <button type="button" className={styles.recurClose} aria-label={t('recur.close')} onClick={closeRecurCreate}>
            <IconClose />
          </button>
        </div>
        <label className={styles.recurLabel} htmlFor="recur-every">
          {t('recur.repeat')}
        </label>
        <div className={styles.recurEvery}>
          <input
            id="recur-every"
            className={styles.recurNumber}
            type="number"
            min="1"
            max="99"
            inputMode="numeric"
            value={every}
            aria-label={t('recur.repeat')}
            onChange={(event) => setEvery(event.target.value)}
          />
          <select
            className={styles.recurUnit}
            value={unit}
            aria-label={t('recur.unit')}
            onChange={(event) => setUnit(event.target.value)}
          >
            {UNITS.map((item) => (
              <option key={item} value={item}>
                {t(count === 1 ? `recur.one.${item}` : `recur.many.${item}`)}
              </option>
            ))}
          </select>
        </div>
        <label className={styles.recurLabel} htmlFor="recur-start">
          {t('recur.start')}
        </label>
        <input
          id="recur-start"
          className={styles.recurDate}
          type="date"
          value={start}
          required
          onChange={(event) => setStart(event.target.value)}
        />
        <div className={styles.recurFoot}>
          <button type="submit" className={styles.primary} disabled={!text.trim()}>
            {t('recur.save')}
          </button>
          <button type="button" className={styles.recurCancel} onClick={closeRecurCreate}>
            {t('recur.cancel')}
          </button>
          <button
            type="button"
            className={styles.recurView}
            onClick={() => openRecurring({ ...draft, text, every: count, unit, start })}
          >
            {t('recur.viewAll')}
          </button>
        </div>
      </form>
    </>
  );
}
